import React, { useState, useEffect } from "react";
import {
  getContactsOverview,
  getOwnerContactsDetail,
  syncOwnerContacts,
  updateSingleContact,
  getAdProfileSyncPreview,
  runAdProfileSync,
  getOrgContacts,
  deleteOrgContact,
  getManualContacts,
  createManualContact,
  updateManualContact,
  deleteManualContact,
  getDuplicateContactsPreview,
  executeDuplicateContactsCleanup,
  getServiceAccounts,
  createServiceAccount,
  updateServiceAccount,
  deleteServiceAccount,
  resolveServiceAccountAdId,
} from "../services/api";
import ProgressBar from "../components/ProgressBar";

const STATUS_LABELS = {
  aktuell: { label: "✅ Aktuell", color: "#107c10" },
  veraltet: { label: "✏️ Veraltet", color: "#d29922" },
  fehlt: { label: "➕ Fehlt", color: "#0078d4" },
  verwaist: { label: "🗑️ Verwaist", color: "#d13438" },
};

function StatusBadge({ status }) {
  const info = STATUS_LABELS[status] || { label: status, color: "#666" };
  return (
    <span style={{ color: info.color, fontWeight: 600, fontSize: 13 }}>
      {info.label}
    </span>
  );
}

/** Zeigt die berechneten Kontaktfelder (Soll-Daten lt. buildContactPayload) eines Kontakts an. */
function ContactPayloadDetails({ payload }) {
  if (!payload) {
    return (
      <div style={{ padding: "8px 12px", color: "#999", fontSize: 12 }}>
        Keine Daten verfügbar (Mitarbeiter nicht mehr im Geltungsbereich).
      </div>
    );
  }
  const addr = payload.businessAddress || {};
  const rows = [
    ["Anzeigename", payload.displayName],
    ["E-Mail", payload.emailAddresses?.[0]?.address],
    ["Position", payload.jobTitle],
    ["Abteilung", payload.department],
    ["Geschäftstelefon", payload.businessPhones?.[0]],
    ["Mobiltelefon", payload.mobilePhone],
    ["Standort/Büro", payload.officeLocation],
    ["Straße", addr.street],
    ["PLZ / Ort", [addr.postalCode, addr.city].filter(Boolean).join(" ")],
    ["Bundesland", addr.state],
    ["Land", addr.countryOrRegion],
  ];
  return (
    <div style={{ padding: "8px 12px 12px" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td style={{ padding: "2px 12px 2px 0", color: "#888", whiteSpace: "nowrap" }}>{label}</td>
              <td style={{ padding: "2px 0", fontWeight: 500 }}>{value || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OwnerDrilldown({ hrlabId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [openContact, setOpenContact] = useState(null);
  const [updatingContact, setUpdatingContact] = useState(null);
  const [syncingOwner, setSyncingOwner] = useState(false);
  const [ownerMessage, setOwnerMessage] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getOwnerContactsDetail(hrlabId)
      .then((res) => setDetail(res.data))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hrlabId]);

  const handleUpdateContact = async (contactHrlabId) => {
    setUpdatingContact(contactHrlabId);
    try {
      const res = await updateSingleContact(hrlabId, contactHrlabId);
      const updated = res.data;
      setDetail((d) => ({
        ...d,
        contacts: d.contacts
          .map((c) => (c.contact_hrlab_id === contactHrlabId ? { ...c, ...updated } : c))
          .filter((c) => updated.status !== "verwaist_entfernt" || c.contact_hrlab_id !== contactHrlabId),
      }));
    } catch (err) {
      alert(`Fehler beim Aktualisieren: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdatingContact(null);
    }
  };

  const handleSyncOwner = async () => {
    setSyncingOwner(true);
    setOwnerMessage(null);
    try {
      const res = await syncOwnerContacts(hrlabId);
      const s = res.data.summary || {};
      setOwnerMessage(
        res.data.status === "success"
          ? `✅ Synchronisiert: ➕ ${s.created || 0} angelegt, ✏️ ${s.updated || 0} aktualisiert, 🗑️ ${s.deleted || 0} entfernt` +
            (s.errors?.length ? ` – ⚠️ ${s.errors.length} Fehler` : "")
          : `❌ Fehler: ${res.data.error || "unbekannt"}`
      );
      load();
    } catch (err) {
      setOwnerMessage(`❌ Fehler: ${err.response?.data?.error || err.message}`);
    } finally {
      setSyncingOwner(false);
    }
  };

  if (loading) return <div style={{ padding: 16 }}><ProgressBar label="Lade Kontakte…" /></div>;
  if (error) return <div style={{ padding: 16, color: "#d13438", fontSize: 13 }}>❌ {error}</div>;
  if (!detail) return null;

  return (
    <div style={{ padding: "8px 16px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12, color: "#666" }}>
          {detail.contacts.length} Kontakt(e) im Mitarbeiterverzeichnis von <strong>{detail.owner.name}</strong>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {ownerMessage && <span style={{ fontSize: 12 }}>{ownerMessage}</span>}
          <button
            onClick={handleSyncOwner}
            disabled={syncingOwner}
            className="btn-secondary"
            style={{ padding: "4px 10px", fontSize: 12 }}
          >
            {syncingOwner ? "⏳ Synchronisiere..." : "🔄 Alle Kontakte dieses Mitarbeiters synchronisieren"}
          </button>
        </div>
      </div>
      <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
              {["", "Kontakt", "E-Mail", "Abteilung", "Status", "Zuletzt aktualisiert", ""].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {detail.contacts.map((c) => (
              <React.Fragment key={c.contact_hrlab_id}>
                <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td
                    style={{ padding: "6px 12px", color: "#999", cursor: "pointer" }}
                    onClick={() => setOpenContact((cur) => (cur === c.contact_hrlab_id ? null : c.contact_hrlab_id))}
                  >
                    {openContact === c.contact_hrlab_id ? "▼" : "▶"}
                  </td>
                  <td style={{ padding: "6px 12px" }}>
                    {c.name}
                    {c.kind === "manual" && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#0078d4", fontWeight: 600 }}>(manuell)</span>
                    )}
                  </td>
                  <td style={{ padding: "6px 12px", color: "#888" }}>{c.email || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>{c.department || "—"}</td>
                  <td style={{ padding: "6px 12px" }}><StatusBadge status={c.status} /></td>
                  <td style={{ padding: "6px 12px", color: "#999" }}>
                    {c.updated_at ? new Date(c.updated_at).toLocaleString("de-DE") : "—"}
                  </td>
                  <td style={{ padding: "6px 12px" }}>
                    <button
                      onClick={() => handleUpdateContact(c.contact_hrlab_id)}
                      disabled={updatingContact === c.contact_hrlab_id}
                      title="Diesen einzelnen Kontakt jetzt neu pushen (Debug)"
                      style={{
                        padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6,
                        background: "#f3f2f1", color: "#323130", border: "1px solid #d2d0ce", cursor: "pointer",
                      }}
                    >
                      {updatingContact === c.contact_hrlab_id ? "⏳..." : "🔄 Aktualisieren"}
                    </button>
                  </td>
                </tr>
                {openContact === c.contact_hrlab_id && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0, background: "#fafbfc" }}>
                      <ContactPayloadDetails payload={c.payload} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdProfileSyncSection() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = () => {
    setLoading(true);
    setError(null);
    getAdProfileSyncPreview()
      .then((res) => setPreview(res.data))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPreview(); }, []);

  const handleRun = async () => {
    if (!window.confirm(
      "Standort-Adressdaten (Büro, Adresse, Fax, Telefon) jetzt für alle betroffenen Mitarbeiter " +
      "in deren Azure-AD-Profil schreiben?"
    )) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await runAdProfileSync();
      const s = res.data.summary || {};
      setResult(
        res.data.status === "success"
          ? `✅ ${s.updated || 0} AD-Profil(e) aktualisiert, ${s.skipped || 0} übersprungen (kein Standort)` +
            (s.errors?.length ? ` – ⚠️ ${s.errors.length} Fehler` : "")
          : `❌ Fehler: ${res.data.error || "unbekannt"}`
      );
      loadPreview();
    } catch (err) {
      setResult(`❌ Fehler: ${err.response?.data?.error || err.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>AD-Profile mit Standorten abgleichen</h2>
          <p style={{ fontSize: 13, color: "#666", margin: "4px 0 0" }}>
            Schreibt Büro/Adresse/Fax/Telefon aus den Standort-Vorlagen (Settings → Standorte) in die
            Azure-AD-Profile aller betroffenen Mitarbeiter – auch bei bereits angelegten Konten. Nützlich,
            nachdem Standorte neu hinterlegt oder geändert wurden.
          </p>
        </div>
        <button className="btn-secondary" onClick={handleRun} disabled={running || loading}>
          {running ? "⏳ Läuft..." : "📍 AD-Profile jetzt synchronisieren"}
        </button>
      </div>

      {running && <ProgressBar label="AD-Profile werden synchronisiert…" />}

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
          ❌ {error}
        </div>
      )}

      {preview && (
        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
          {preview.scope_count} Mitarbeiter im Geltungsbereich · <strong>{preview.with_template}</strong> mit zugewiesenem
          Standort (werden aktualisiert) · {preview.without_template} ohne Standort-Vorlage
        </p>
      )}

      {result && <p style={{ fontSize: 13, marginTop: 8 }}>{result}</p>}
    </div>
  );
}

function OrgContactsSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getOrgContacts()
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Org-Kontakt "${item.displayName}" (${item.email || "ohne E-Mail"}) wirklich endgültig löschen?`)) return;
    setDeletingId(item.id);
    try {
      await deleteOrgContact(item.id);
      setData((d) => ({
        ...d,
        total: d.total - 1,
        matched: d.matched - (item.matched_employee ? 1 : 0),
        items: d.items.filter((i) => i.id !== item.id),
      }));
    } catch (err) {
      alert(`Fehler beim Löschen: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Alte Organisations-Kontakte</h2>
          <p style={{ fontSize: 13, color: "#666", margin: "4px 0 0" }}>
            Kontakte aus dem ursprünglichen Rollout (org-weit über die Microsoft-Graph-API angelegt,
            unabhängig von der neuen persönlichen Kontaktverteilung). Einträge, die einem aktuellen
            Mitarbeiter zugeordnet werden konnten, sind potenzielle Duplikate und können hier entfernt werden.
          </p>
        </div>
        <button className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? "⏳ Lädt..." : "🔍 Org-Kontakte laden"}
        </button>
      </div>

      {loading && <ProgressBar label="Org-Kontakte werden geladen…" />}

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
          ❌ {error}
        </div>
      )}

      {data && (
        data.items.length === 0 ? (
          <div style={{ padding: "12px 16px", borderRadius: 8, background: "#dff6dd", color: "#107c10", fontSize: 14, fontWeight: 600 }}>
            ✅ Keine alten Organisations-Kontakte gefunden.
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 12px" }}>
              {data.total} Kontakt(e) gesamt · <strong>{data.matched}</strong> davon einem aktuellen Mitarbeiter zugeordnet
            </p>
            <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                    {["Name", "E-Mail", "Abteilung", "Position", "Zugeordneter Mitarbeiter", ""].map((h) => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "6px 12px" }}>{item.displayName}</td>
                      <td style={{ padding: "6px 12px", color: "#888" }}>{item.email || "—"}</td>
                      <td style={{ padding: "6px 12px" }}>{item.department || "—"}</td>
                      <td style={{ padding: "6px 12px" }}>{item.jobTitle || "—"}</td>
                      <td style={{ padding: "6px 12px" }}>
                        {item.matched_employee ? (
                          <span style={{ color: item.matched_employee.status === "active" ? "#107c10" : "#d13438" }}>
                            {item.matched_employee.name} ({item.matched_employee.status})
                          </span>
                        ) : (
                          <span style={{ color: "#999" }}>kein Treffer</span>
                        )}
                      </td>
                      <td style={{ padding: "6px 12px" }}>
                        <button
                          onClick={() => handleDelete(item)}
                          disabled={deletingId === item.id}
                          style={{
                            padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6,
                            background: "#fde7e9", color: "#d13438", border: "1px solid #f3c0c2", cursor: "pointer",
                          }}
                        >
                          {deletingId === item.id ? "⏳..." : "🗑️ Löschen"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </div>
  );
}

function DuplicateContactsSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    setResult(null);
    getDuplicateContactsPreview()
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  const handleCleanup = async () => {
    if (!data || data.total_duplicates === 0) return;
    if (!window.confirm(`${data.total_duplicates} doppelte Kontakt(e) bei ${data.affected_owners} Mitarbeiter(n) jetzt entfernen?`)) return;
    setCleaning(true);
    setError(null);
    try {
      const res = await executeDuplicateContactsCleanup();
      setResult(res.data.summary);
      load();
    } catch (err) {
      alert(`Fehler bei der Bereinigung: ${err.response?.data?.error || err.message}`);
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Doppelte Kontakte</h2>
          <p style={{ fontSize: 13, color: "#666", margin: "4px 0 0" }}>
            Findet doppelte Einträge im "Mitarbeiterverzeichnis"-Ordner der Mitarbeiter (z.B. nach
            früheren Daten-Resets entstanden) und entfernt die überzähligen Kopien. Der jeweils
            aktuell verwaltete Kontakt (bzw. der älteste, falls keiner getrackt ist) bleibt erhalten.
          </p>
        </div>
        <button className="btn-secondary" onClick={load} disabled={loading || cleaning}>
          {loading ? "⏳ Lädt..." : "🔍 Prüfen"}
        </button>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
          ❌ {error}
        </div>
      )}

      {result && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#dff6dd", color: "#107c10", fontSize: 14 }}>
          ✅ {result.removed} doppelte Kontakt(e) bei {result.owners_processed} Mitarbeiter(n) entfernt.
          {result.errors?.length > 0 && (
            <span style={{ color: "#d13438" }}> · {result.errors.length} Fehler</span>
          )}
        </div>
      )}

      {!data && !error && (
        loading ? (
          <ProgressBar label="Suche nach doppelten Kontakten…" />
        ) : (
          <div style={{ padding: "12px 16px", borderRadius: 8, background: "#f8f9fa", color: "#666", fontSize: 14 }}>
            Noch nicht geprüft – auf "🔍 Prüfen" klicken, um nach doppelten Kontakten im Mitarbeiterverzeichnis zu suchen.
          </div>
        )
      )}

      {cleaning && <ProgressBar label="Entferne doppelte Kontakte…" />}

      {data && (
        data.total_duplicates === 0 ? (
          <div style={{ padding: "12px 16px", borderRadius: 8, background: "#dff6dd", color: "#107c10", fontSize: 14, fontWeight: 600 }}>
            ✅ Keine doppelten Kontakte gefunden ({data.scope_count} Mitarbeiter geprüft).
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 12px" }}>
              <strong>{data.total_duplicates}</strong> doppelte Kontakt(e) bei <strong>{data.affected_owners}</strong> von {data.scope_count} Mitarbeiter(n) gefunden.
            </p>
            <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8, marginBottom: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                    {["Mitarbeiter", "E-Mail", "Doppelte Kontakte", "Details"].map((h) => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.owners.map((o) => (
                    <tr key={o.hrlab_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "6px 12px" }}>{o.name}</td>
                      <td style={{ padding: "6px 12px", color: "#888" }}>{o.email}</td>
                      <td style={{ padding: "6px 12px" }}>
                        {o.error ? (
                          <span style={{ color: "#d13438" }}>Fehler: {o.error}</span>
                        ) : (
                          <span style={{ fontWeight: 600 }}>{o.duplicate_count}</span>
                        )}
                      </td>
                      <td style={{ padding: "6px 12px", color: "#666" }}>
                        {!o.error && o.duplicate_groups?.map((g) => `${g.displayName} (${g.count}x)`).join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleCleanup}
              disabled={cleaning}
              style={{
                padding: "6px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6,
                background: "#d13438", color: "#fff", border: "none",
                cursor: cleaning ? "default" : "pointer",
                opacity: cleaning ? 0.6 : 1,
              }}
            >
              {cleaning ? "⏳ Bereinige..." : "🗑️ Duplikate jetzt entfernen"}
            </button>
          </>
        )
      )}
    </div>
  );
}

// ─── Service-Accounts Section ─────────────────────────────────────

/**
 * Verwaltung von Service-Accounts: Outlook-Konten außerhalb von HRLab
 * (z.B. info@, drucker@, sekretariat@), die trotzdem das
 * Mitarbeiterverzeichnis in ihr persönliches Postfach gespiegelt bekommen.
 */
function ServiceAccountsSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ email: "", label: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getServiceAccounts()
      .then((res) => setAccounts(res.data.accounts || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({ email: "", label: "", notes: "" });
    setShowForm(true);
    setMessage(null);
  };

  const openEdit = (acc) => {
    setEditingId(acc.id);
    setForm({ email: acc.email, label: acc.label || "", notes: acc.notes || "" });
    setShowForm(true);
    setMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await updateServiceAccount(editingId, form);
        setMessage("✅ Service-Account aktualisiert.");
      } else {
        await createServiceAccount(form);
        setMessage("✅ Service-Account angelegt – erhält ab dem nächsten Kontakt-Sync das Mitarbeiterverzeichnis.");
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err) {
      alert(`Fehler: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (acc) => {
    setResolvingId(acc.id);
    try {
      const res = await resolveServiceAccountAdId(acc.id);
      const updated = res.data.account;
      setAccounts((prev) => prev.map((a) => (a.id === acc.id ? updated : a)));
      setMessage(
        updated.ad_user_id
          ? `✅ Azure-AD-Konto für ${acc.email} gefunden (ID: ${updated.ad_user_id.slice(0, 8)}…).`
          : `⚠️ Kein Azure-AD-Konto für ${acc.email} gefunden – bitte E-Mail prüfen.`
      );
    } catch (err) {
      alert(`Fehler: ${err.response?.data?.error || err.message}`);
    } finally {
      setResolvingId(null);
    }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`Service-Account "${acc.email}" wirklich löschen? Kontakt-Tracking-Daten werden ebenfalls entfernt.`)) return;
    setDeletingId(acc.id);
    try {
      await deleteServiceAccount(acc.id);
      setAccounts((prev) => prev.filter((a) => a.id !== acc.id));
      setMessage("✅ Service-Account gelöscht.");
    } catch (err) {
      alert(`Fehler: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const inputStyle = { padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid #d2d0ce", width: "100%", boxSizing: "border-box" };
  const labelStyle = { fontSize: 12, color: "#666", marginBottom: 2, display: "block" };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Service-Accounts</h2>
          <p style={{ fontSize: 13, color: "#666", margin: "4px 0 0" }}>
            Outlook-Konten außerhalb von HRLab (z.B. <em>info@</em>, <em>drucker@</em>, <em>sekretariat@</em>),
            die trotzdem das Mitarbeiterverzeichnis in ihr persönliches Postfach gespiegelt bekommen.
            Sie erscheinen selbst nicht als Kontakt bei anderen.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? "⏳ Lädt..." : "↻ Aktualisieren"}
          </button>
          <button className="btn-secondary" onClick={openNew}>
            ➕ Service-Account hinzufügen
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
          ❌ {error}
        </div>
      )}

      {message && <p style={{ fontSize: 13, color: "#107c10", margin: "0 0 12px" }}>{message}</p>}

      {showForm && (
        <div style={{ padding: 16, background: "#fafbfc", border: "1px solid #e1e4e8", borderRadius: 8, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>E-Mail des Outlook-Kontos *</label>
              <input
                style={inputStyle}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="info@fsnw.de"
                disabled={!!editingId}
              />
            </div>
            <div>
              <label style={labelStyle}>Bezeichnung</label>
              <input
                style={inputStyle}
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="z.B. Info-Postfach"
              />
            </div>
            <div>
              <label style={labelStyle}>Notizen</label>
              <input
                style={inputStyle}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional"
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              onClick={handleSave}
              disabled={saving || !form.email.trim()}
              style={{
                padding: "6px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6,
                background: "#0078d4", color: "#fff", border: "none",
                cursor: saving || !form.email.trim() ? "default" : "pointer",
                opacity: saving || !form.email.trim() ? 0.6 : 1,
              }}
            >
              {saving ? "⏳ Speichern..." : "💾 Speichern"}
            </button>
            <button className="btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>Abbrechen</button>
          </div>
        </div>
      )}

      {accounts.length === 0 && !loading ? (
        <p style={{ fontSize: 13, color: "#999" }}>Keine Service-Accounts angelegt.</p>
      ) : (
        <div style={{ border: "1px solid #e1e4e8", borderRadius: 8, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                {["E-Mail", "Bezeichnung", "Azure-AD-Status", "Notizen", ""].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accounts.map((acc) => (
                <tr key={acc.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "6px 12px", fontWeight: 500 }}>{acc.email}</td>
                  <td style={{ padding: "6px 12px" }}>{acc.label || <span style={{ color: "#999" }}>—</span>}</td>
                  <td style={{ padding: "6px 12px" }}>
                    {acc.ad_user_id ? (
                      <span style={{ color: "#107c10", fontWeight: 600 }}>✅ Verknüpft</span>
                    ) : (
                      <span style={{ color: "#d13438", fontWeight: 600 }}>⚠️ Kein AD-Konto</span>
                    )}
                  </td>
                  <td style={{ padding: "6px 12px", color: "#888" }}>{acc.notes || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {!acc.ad_user_id && (
                        <button
                          onClick={() => handleResolve(acc)}
                          disabled={resolvingId === acc.id}
                          title="Azure-AD-Konto per E-Mail suchen"
                          style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, background: "#fff4ce", color: "#8a5800", border: "1px solid #f0d080", cursor: "pointer" }}
                        >
                          {resolvingId === acc.id ? "⏳..." : "🔍 AD auflösen"}
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(acc)}
                        style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, background: "#f3f2f1", color: "#323130", border: "1px solid #d2d0ce", cursor: "pointer" }}
                      >
                        ✏️ Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(acc)}
                        disabled={deletingId === acc.id}
                        style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, background: "#fde7e9", color: "#d13438", border: "1px solid #f3c0c2", cursor: "pointer" }}
                      >
                        {deletingId === acc.id ? "⏳..." : "🗑️ Löschen"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EMPTY_MANUAL_CONTACT = {
  display_name: "", first_name: "", last_name: "", email: "",
  job_title: "", company: "", business_phone: "", mobile_phone: "",
  office_location: "", street_address: "", postal_code: "", city: "",
  state: "", country: "", notes: "",
};

const MANUAL_CONTACT_FIELDS = [
  ["display_name", "Anzeigename *"],
  ["first_name", "Vorname"],
  ["last_name", "Nachname"],
  ["email", "E-Mail"],
  ["job_title", "Position/Funktion"],
  ["company", "Firma/Abteilung"],
  ["business_phone", "Telefon (geschäftlich)"],
  ["mobile_phone", "Telefon (mobil)"],
  ["office_location", "Standort/Büro"],
  ["street_address", "Straße"],
  ["postal_code", "PLZ"],
  ["city", "Ort"],
  ["state", "Bundesland"],
  ["country", "Land"],
];

/** Formular zum Anlegen/Bearbeiten eines manuellen Kontakts (externe Person/Stelle ohne Mitarbeiter-Datensatz). */
function ManualContactForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...EMPTY_MANUAL_CONTACT, ...initial });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const inputStyle = { padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "1px solid #d2d0ce", width: "100%", boxSizing: "border-box" };
  const labelStyle = { fontSize: 12, color: "#666", marginBottom: 2, display: "block" };

  return (
    <div style={{ padding: 16, background: "#fafbfc", border: "1px solid #e1e4e8", borderRadius: 8, marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
        {MANUAL_CONTACT_FIELDS.map(([key, label]) => (
          <div key={key}>
            <label style={labelStyle}>{label}</label>
            <input style={inputStyle} value={form[key] || ""} onChange={set(key)} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Notizen</label>
        <textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes || ""} onChange={set("notes")} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.display_name?.trim()}
          style={{
            padding: "6px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6,
            background: "#0078d4", color: "#fff", border: "none",
            cursor: saving || !form.display_name?.trim() ? "default" : "pointer",
            opacity: saving || !form.display_name?.trim() ? 0.6 : 1,
          }}
        >
          {saving ? "⏳ Speichern..." : "💾 Speichern"}
        </button>
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>Abbrechen</button>
      </div>
    </div>
  );
}

/**
 * Verwaltung manueller Kontakte: externe Personen/Stellen ohne
 * Mitarbeiter-Datensatz (z.B. Lieferanten, Behörden, Partner). Werden wie
 * Mitarbeiter-Kontakte automatisch in das "Mitarbeiterverzeichnis" jedes
 * Mitarbeiters verteilt (siehe getContactTargets im Backend).
 */
function ManualContactsSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getManualContacts()
      .then((res) => setContacts(res.data.contacts || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await createManualContact(form);
      setShowForm(false);
      setMessage("✅ Kontakt angelegt – wird mit der nächsten Kontaktverteilung in alle Mitarbeiterverzeichnisse übernommen.");
      load();
    } catch (err) {
      alert(`Fehler beim Anlegen: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await updateManualContact(editingId, form);
      setEditingId(null);
      setMessage("✅ Kontakt aktualisiert – Änderungen werden mit der nächsten Kontaktverteilung übernommen.");
      load();
    } catch (err) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Manuellen Kontakt "${contact.display_name}" wirklich löschen? Er wird aus allen Mitarbeiterverzeichnissen entfernt.`)) return;
    setDeletingId(contact.id);
    try {
      await deleteManualContact(contact.id);
      setMessage("✅ Kontakt gelöscht – wird mit der nächsten Kontaktverteilung aus allen Mitarbeiterverzeichnissen entfernt.");
      setContacts((cs) => cs.filter((c) => c.id !== contact.id));
    } catch (err) {
      alert(`Fehler beim Löschen: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const editingContact = editingId ? contacts.find((c) => c.id === editingId) : null;

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Manuelle Kontakte</h2>
          <p style={{ fontSize: 13, color: "#666", margin: "4px 0 0" }}>
            Externe Personen/Stellen ohne Mitarbeiter-Datensatz (z.B. Lieferanten, Behörden, Partner).
            Werden wie Mitarbeiter-Kontakte in das "Mitarbeiterverzeichnis" jedes Mitarbeiters verteilt –
            Änderungen werden erst bei der nächsten Kontaktverteilung übernommen, nicht sofort.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? "⏳ Lädt..." : "↻ Aktualisieren"}
          </button>
          <button
            className="btn-secondary"
            onClick={() => { setEditingId(null); setShowForm((s) => !s); }}
          >
            {showForm ? "Abbrechen" : "➕ Neuer Kontakt"}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
          ❌ {error}
        </div>
      )}

      {message && <p style={{ fontSize: 13, color: "#107c10", margin: "0 0 12px" }}>{message}</p>}

      {showForm && (
        <ManualContactForm
          initial={EMPTY_MANUAL_CONTACT}
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      )}

      {editingContact && (
        <ManualContactForm
          initial={editingContact}
          onSave={handleUpdate}
          onCancel={() => setEditingId(null)}
          saving={saving}
        />
      )}

      {contacts.length === 0 ? (
        <p style={{ fontSize: 13, color: "#999" }}>Keine manuellen Kontakte angelegt.</p>
      ) : (
        <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                {["Name", "E-Mail", "Position", "Firma", "Telefon", "Standort", ""].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "6px 12px", fontWeight: 500 }}>{c.display_name}</td>
                  <td style={{ padding: "6px 12px", color: "#888" }}>{c.email || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>{c.job_title || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>{c.company || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>{c.business_phone || c.mobile_phone || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>{c.office_location || "—"}</td>
                  <td style={{ padding: "6px 12px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={() => { setShowForm(false); setEditingId(c.id); }}
                        style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, background: "#f3f2f1", color: "#323130", border: "1px solid #d2d0ce", cursor: "pointer" }}
                      >
                        ✏️ Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        disabled={deletingId === c.id}
                        style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, background: "#fde7e9", color: "#d13438", border: "1px solid #f3c0c2", cursor: "pointer" }}
                      >
                        {deletingId === c.id ? "⏳..." : "🗑️ Löschen"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ContactsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getContactsOverview()
      .then((res) => setOverview(res.data))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleExpand = (hrlabId) => {
    setExpanded((cur) => (cur === hrlabId ? null : hrlabId));
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Kontakte</h1>
        <p style={{ color: "#666", marginTop: 4 }}>
          Übersicht über die persönlichen Outlook-Kontakte aller Mitarbeiter (Ordner "Mitarbeiterverzeichnis")
          sowie Bereinigung der alten org-weiten Kontakte.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Mitarbeiter &amp; ihre Kontakte</h2>
          <button className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? "⏳ Lädt..." : "↻ Aktualisieren"}
          </button>
        </div>

        {error && (
          <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
            ❌ {error}
          </div>
        )}

        {overview && (
          <>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 12px" }}>
              {overview.scope_count} Mitarbeiter im Geltungsbereich (aktiver Azure-AD-Account + aktiver HRLab-Status) ·{" "}
              insgesamt <strong>➕ {overview.totals.create}</strong>,{" "}
              <strong>✏️ {overview.totals.update}</strong>,{" "}
              <strong>🗑️ {overview.totals.delete}</strong> offen{" "}
              ({overview.totals.unchanged} unverändert)
              {overview.inSync && <span style={{ color: "#107c10", fontWeight: 600 }}> – alles synchron ✅</span>}
            </p>

            <div style={{ maxHeight: 600, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                    {["", "Mitarbeiter", "E-Mail", "Abteilung", "Kontakte", "➕ Neu", "✏️ Geändert", "🗑️ Entfernen", "Zuletzt aktualisiert"].map((h) => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overview.owners.map((o) => (
                    <React.Fragment key={o.hrlab_id}>
                      <tr
                        style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                        onClick={() => toggleExpand(o.hrlab_id)}
                        onMouseOver={(e) => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "6px 12px", color: "#999" }}>{expanded === o.hrlab_id ? "▼" : "▶"}</td>
                        <td style={{ padding: "6px 12px", fontWeight: 500 }}>{o.name}</td>
                        <td style={{ padding: "6px 12px", color: "#888" }}>{o.email}</td>
                        <td style={{ padding: "6px 12px" }}>{o.department || "—"}</td>
                        <td style={{ padding: "6px 12px" }}>{o.contact_count}</td>
                        <td style={{ padding: "6px 12px" }}>{o.toCreate || "—"}</td>
                        <td style={{ padding: "6px 12px" }}>{o.toUpdate || "—"}</td>
                        <td style={{ padding: "6px 12px" }}>{o.toDelete || "—"}</td>
                        <td style={{ padding: "6px 12px", color: "#999" }}>
                          {o.last_updated ? new Date(o.last_updated).toLocaleString("de-DE") : "—"}
                        </td>
                      </tr>
                      {expanded === o.hrlab_id && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, background: "#fafbfc" }}>
                            <OwnerDrilldown hrlabId={o.hrlab_id} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <ServiceAccountsSection />

      <ManualContactsSection />

      <AdProfileSyncSection />

      <DuplicateContactsSection />

      <OrgContactsSection />
    </div>
  );
}
